'use client';

import { useState, useEffect, useRef, useCallback } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Menu, X, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { name: "Features", href: "/features" },
  { name: "How it works", href: "/how-it-works" },
  { name: "Connect", href: "/connect" },
  { name: "Docs", href: "/docs" },
  { name: "FAQ", href: "/faq" },
  { name: "Demo", href: "/demo" },
  { name: "Pricing", href: "/pricing" },
  { name: "Blog", href: "/blog" },
  { name: "Compare", href: "/compare" },
];

const platformLinks = [
  {
    href: "/platforms/webflow",
    icon: "🔷",
    name: "Webflow",
    desc: "One-click install from the Marketplace",
    badge: "Live",
    badgeClass: "bg-green-100 text-green-700",
  },
  {
    href: "/platforms/wordpress",
    icon: "📝",
    name: "WordPress",
    desc: "Plugin download — paste key and go",
    badge: "Live",
    badgeClass: "bg-green-100 text-green-700",
  },
  {
    href: "/platforms/drupal",
    icon: "💧",
    name: "Drupal",
    desc: "Composer install, uses core JSON:API",
    badge: "Live",
    badgeClass: "bg-green-100 text-green-700",
  },
  {
    href: "/platforms",
    icon: "🔧",
    name: "More platforms",
    desc: "Ghost, Shopify and others — coming soon",
    badge: null,
    badgeClass: "",
  },
];

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [platformsOpen, setPlatformsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => { setMounted(true); }, []);

  const handleTryFree = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (pathname === "/") {
      // focus the hero input if already on the homepage
      const input = document.querySelector<HTMLInputElement>("#hero input");
      if (input) { input.focus(); input.scrollIntoView({ behavior: "smooth", block: "center" }); return; }
    }
    // From any other page, go directly to the preview/analyze page
    router.push("/preview");
  }, [pathname, router]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setMobileMenuOpen(false);
    setPlatformsOpen(false);
  }, [pathname]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setPlatformsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const hideNav = ["/login", "/signup", "/onboarding", "/checkout", "/billing/success"].some(
    (p) => pathname === p || pathname?.startsWith(p + "/") || pathname?.startsWith(p + "?")
  );
  // Only hide AFTER mount to avoid React #418 hydration mismatch.
  if (mounted && hideNav) return null;

  return (
    <>
      <header
        className={cn(
          "fixed top-0 w-full z-50 transition-all duration-300 border-b border-transparent",
          isScrolled
            ? "bg-white/80 backdrop-blur-md border-border shadow-sm py-3"
            : "bg-transparent py-5"
        )}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link href="/" className="flex items-center mr-8">
              <picture>
                <source srcSet="/images/legible-logo.webp" type="image/webp" />
                <img
                  src="/images/legible-logo.png"
                  alt="Legible"
                  className="h-10 w-auto"
                  width={138}
                  height={40}
                  fetchPriority="high"
                />
              </picture>
            </Link>

            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  href={link.href}
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  {link.name}
                </Link>
              ))}

              {/* Platforms dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setPlatformsOpen((o) => !o)}
                  className="flex items-center gap-0.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Platforms
                  <ChevronDown
                    className={cn("w-3.5 h-3.5 mt-px transition-transform duration-200", platformsOpen && "rotate-180")}
                  />
                </button>

                <AnimatePresence>
                  {platformsOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 6, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 6, scale: 0.97 }}
                      transition={{ duration: 0.15 }}
                      className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-72 bg-white border border-border rounded-2xl shadow-xl overflow-hidden"
                    >
                      <div className="p-2">
                        {platformLinks.map((p) => (
                          <Link
                            key={p.href}
                            href={p.href}
                            className="flex items-start gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors group"
                          >
                            <span className="text-xl mt-0.5 shrink-0">{p.icon}</span>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                                  {p.name}
                                </span>
                                {p.badge && (
                                  <span className={cn("text-[10px] font-bold px-1.5 py-0.5 rounded-full", p.badgeClass)}>
                                    {p.badge}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{p.desc}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </nav>

            <div className="hidden md:flex items-center gap-5">
              <Link
                href="/login/"
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Sign in
              </Link>
              <button
                onClick={handleTryFree}
                className="inline-flex items-center justify-center px-5 py-2 text-sm font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Try it free
              </button>
            </div>

            <button
              className="md:hidden p-2 text-foreground"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-background pt-24 px-4 pb-6 flex flex-col md:hidden overflow-y-auto"
          >
            <nav className="flex flex-col gap-6 text-lg font-medium">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  href={link.href}
                  className="text-foreground hover:text-primary transition-colors"
                >
                  {link.name}
                </Link>
              ))}

              {/* Mobile platforms section */}
              <div>
                <p className="text-foreground mb-3">Platforms</p>
                <div className="flex flex-col gap-1 pl-4 border-l-2 border-border">
                  {platformLinks.map((p) => (
                    <Link
                      key={p.href}
                      href={p.href}
                      className="flex items-center gap-2 py-1.5 text-base text-muted-foreground hover:text-primary transition-colors"
                    >
                      <span className="text-base">{p.icon}</span>
                      {p.name}
                      {p.badge && (
                        <span className={cn("text-[10px] font-bold px-1.5 py-0.5 rounded-full", p.badgeClass)}>
                          {p.badge}
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>

              <hr className="border-border my-2" />
              <Link href="/login/" className="text-foreground">Sign in</Link>
              <button
                onClick={(e) => { setMobileMenuOpen(false); handleTryFree(e); }}
                className="inline-flex justify-center items-center gap-2 px-6 py-3 mt-4 text-base font-semibold rounded-xl bg-primary text-primary-foreground"
              >
                Try it free
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
